/**
 * AudienceParcoursHub
 * ─────────────────────────────────────────────────────────────────
 * Hub générique tone-aware pour les pages liste de parcours/travaux/
 * opérations/patrimoine d'une audience (hors particuliers).
 *
 * Sections :
 *  - Hero éditorial
 *  - Grille des items (1 carte par ParcoursStub)
 *  - CTA final tone-aware
 *
 * Consommé par /[audience]/[segment] pour pro, entreprise, tertiaire,
 * industrie, collectivités, copropriétés, artisans.
 */

import { ArrowRight, CheckCircle2 } from 'lucide-react';
import type { AudienceId } from '@shared/constants/audiences';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { BreadcrumbListLd } from '../seo/jsonld/BreadcrumbList';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import type { ParcoursStub } from '../../config/parcours-particuliers';
import { getStubTokens, getAudienceBreadcrumbLabel, SITE_URL } from '../audience/_shared/tone-tokens';
import { getAudienceById } from '@shared/constants/audiences';

interface Props {
  audienceId: AudienceId;
  /** Segment URL (ex. "travaux", "operations", "patrimoine", "catalogue", "process-ag") */
  segment: string;
  /** Libellé visible du segment dans le breadcrumb */
  segmentLabel: string;
  /** Liste des items à présenter */
  items: ParcoursStub[];
  /** Eyebrow du hero */
  heroEyebrow: string;
  /** Titre principal */
  heroHeadline: string;
  /** Titre — partie accent */
  heroHeadlineAccent: string;
  /** Paragraphe d'introduction */
  heroIntro: string;
  /** CTA principal hero */
  primaryCta: { label: string; href: string };
  /** CTA secondaire hero */
  secondaryCta?: { label: string; href: string };
  /** Bullets sous CTAs (3 max) */
  bullets?: string[];
  /** Titre section grid */
  gridTitle: string;
  /** Eyebrow section grid */
  gridEyebrow: string;
  /** Titre CTA final */
  ctaFinalTitle: string;
  /** Description CTA final */
  ctaFinalDescription: string;
}

export function AudienceParcoursHub({
  audienceId,
  segment,
  segmentLabel,
  items,
  heroEyebrow,
  heroHeadline,
  heroHeadlineAccent,
  heroIntro,
  primaryCta,
  secondaryCta,
  bullets = ['Gratuit · sans inscription', 'Sources officielles citées', 'Sans engagement'],
  gridTitle,
  gridEyebrow,
  ctaFinalTitle,
  ctaFinalDescription,
}: Props) {
  const t = getStubTokens(audienceId);
  const audience = getAudienceById(audienceId);
  const audienceHref = audience?.href ?? '/';
  const audienceLabel = getAudienceBreadcrumbLabel(audienceId);
  const segmentHref = `${audienceHref}/${segment}`;

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${SITE_URL}/` },
          { name: audienceLabel, url: `${SITE_URL}${audienceHref}` },
          { name: segmentLabel, url: `${SITE_URL}${segmentHref}` },
        ]}
      />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-stone-50 to-paper pt-12 pb-12 md:pt-16 md:pb-16">
        <div aria-hidden className={cn('pointer-events-none absolute inset-0', t.heroHalo)} />
        <Container size="narrow">
          <nav aria-label="Fil d'Ariane" className="mb-6 text-[12px] text-stone-500">
            <a href={audienceHref} className={cn('transition-colors', t.linkColor)}>
              {audienceLabel}
            </a>
            <span className="mx-1.5 text-stone-300">/</span>
            <span className="font-medium text-stone-700">{segmentLabel}</span>
          </nav>

          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]',
              t.eyebrowChip,
            )}
          >
            {heroEyebrow}
          </span>

          <h1 className="mt-4 font-display text-4xl font-black tracking-tight text-ink md:text-5xl">
            {heroHeadline}{' '}
            <span className={t.gradientText}>{heroHeadlineAccent}</span>
          </h1>

          <p className="mt-4 text-lg text-stone-600 md:text-xl">{heroIntro}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" variant={t.buttonVariant} className={cn('group', t.ctaShadow)}>
              <a href={primaryCta.href}>
                {primaryCta.label}
                <ArrowRight className="h-4 w-4 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
              </a>
            </Button>
            {secondaryCta && (
              <Button asChild size="lg" variant="ghost">
                <a href={secondaryCta.href}>{secondaryCta.label}</a>
              </Button>
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
            {bullets.map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5 text-[12px] text-stone-500">
                <CheckCircle2 className={cn('h-3.5 w-3.5 shrink-0', t.checkColor)} strokeWidth={2} />
                {b}
              </span>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Grid items ── */}
      <section className="py-14 md:py-20">
        <Container size="narrow">
          <div className="mb-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">
              {gridEyebrow}
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-ink md:text-3xl">
              {gridTitle}
            </h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => {
              const Icon = getIcon(item.iconName);
              return (
                <a
                  key={item.slug}
                  href={`${segmentHref}/${item.slug}`}
                  className="group flex flex-col gap-3 rounded-2xl border border-stone-200 bg-paper p-5 shadow-[0_1px_3px_rgba(10,10,10,.04)] transition-all duration-150 ease-out hover:-translate-y-px hover:border-stone-300 hover:shadow-[0_8px_28px_-8px_rgba(10,10,10,.10)]"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        'inline-flex h-10 w-10 items-center justify-center rounded-lg ring-1 ring-inset transition-transform duration-150 ease-out group-hover:scale-105',
                        t.iconChipBg,
                        t.iconChipText,
                        t.iconChipRing,
                      )}
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.7} />
                    </span>
                    <span className={cn('text-[11px] font-bold tabular-nums', t.linkColor.split(' ')[0])}>
                      {item.badge}
                    </span>
                  </div>

                  <div>
                    <p className="font-display text-lg font-bold text-ink">{item.title}</p>
                    <p className="mt-1 text-[12px] text-stone-500">{item.badgeLabel}</p>
                  </div>

                  <p className="line-clamp-3 text-[13px] leading-relaxed text-stone-600">
                    {item.lead}
                  </p>

                  <div className="mt-auto flex items-center justify-between pt-2">
                    <span className="text-[10px] uppercase tracking-[0.12em] text-stone-400">
                      {item.eyebrow}
                    </span>
                    <span className={cn('inline-flex items-center gap-1 text-[12px] font-semibold transition-transform duration-150 ease-out group-hover:translate-x-0.5', t.linkColor)}>
                      Découvrir
                      <ArrowRight className="h-3 w-3" strokeWidth={2.4} />
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ── CTA final ── */}
      <section className="pb-20">
        <Container size="narrow">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-ink via-stone-900 to-ink p-8 text-center md:p-12">
            <div
              aria-hidden
              className={cn(
                'pointer-events-none absolute -inset-x-12 -bottom-16 h-32 rounded-full blur-3xl',
                t.ctaFinalAccentHalo,
              )}
            />
            <div className="relative">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">
                Diagnostic personnalisé
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold text-ivory-50 md:text-3xl">
                {ctaFinalTitle}
              </h2>
              <p className="mt-3 text-stone-400">{ctaFinalDescription}</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button asChild size="lg" variant={t.buttonVariant}>
                  <a href={primaryCta.href}>
                    {primaryCta.label}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href={`/contact?source=${audienceId}`}>Parler à un conseiller</a>
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
